import { TranslateService } from '@ngx-translate/core';

/** Timezone do estabelecimento — todas as comparacoes de horario devem usar este fuso */
export const CARDAPIO_TIMEZONE = 'America/Asuncion';

/** Horario parseado de um dia da semana */
export interface HorarioDia {
  dia: string;
  horario: string;
  fechado: boolean;
  atual: boolean;
}

/**
 * Retorna hora, minuto e indice do dia da semana (0=domingo) no fuso do estabelecimento,
 * independente do fuso configurado no navegador do cliente.
 */
function obterAgoraEmAsuncion(): { hora: number; minuto: number; diaIndex: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: CARDAPIO_TIMEZONE,
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts = fmt.formatToParts(new Date());
  const hora = +parts.find(p => p.type === 'hour')!.value;
  const minuto = +parts.find(p => p.type === 'minute')!.value;
  const weekday = parts.find(p => p.type === 'weekday')!.value; // 'Sun', 'Mon', ..., 'Sat'
  const WEEKDAY_TO_INDEX: Record<string, number> = {
    'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6,
  };
  const diaIndex = WEEKDAY_TO_INDEX[weekday] ?? 0;
  return { hora, minuto, diaIndex };
}

/** Mapa de nomes de dias (backend pode retornar pt-BR abreviado ou espanhol completo) para chave interna */
const DIAS_ALIAS_MAP: Record<string, string> = {
  // Portugues abreviado (formato atual do backend CardapioViewMapper)
  'seg': 'seg', 'ter': 'ter', 'qua': 'qua', 'qui': 'qui', 'sex': 'sex', 'sab': 'sab', 'dom': 'dom',
  // Espanhol completo
  'lunes': 'seg', 'martes': 'ter', 'miercoles': 'qua', 'miércoles': 'qua',
  'jueves': 'qui', 'viernes': 'sex', 'sabado': 'sab', 'sábado': 'sab', 'domingo': 'dom',
  // Portugues completo
  'segunda': 'seg', 'terca': 'ter', 'terça': 'ter', 'quarta': 'qua',
  'quinta': 'qui', 'sexta': 'sex',
};

/** Ordem dos dias para exibicao (segunda a domingo) */
const DIAS_ORDEM = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];

/** Indice JS do dia da semana (0=domingo) para cada chave interna */
const DIAS_INDEX_MAP: Record<string, number> = {
  'dom': 0, 'seg': 1, 'ter': 2, 'qua': 3, 'qui': 4, 'sex': 5, 'sab': 6,
};

/**
 * Calcula se a loja esta aberta agora com base na string horarioFuncionamento,
 * usando o fuso do estabelecimento (America/Asuncion), nao o fuso do navegador.
 *
 * Formato esperado: "Seg: 07:00 - 12:00, 13:30 - 18:00 | Ter: Cerrado | ..."
 *
 * @param raw - String bruta de horarioFuncionamento do backend
 * @returns true se estiver dentro de algum intervalo do dia atual
 */
export function calcularSeAbertoLocal(raw: string | null | undefined): boolean {
  if (!raw) return true; // sem horario configurado, considera aberto

  // Usar horario no fuso do estabelecimento, nao do navegador do cliente
  const { hora, minuto, diaIndex: diaJsIndex } = obterAgoraEmAsuncion();

  // Converter indice JS para chave interna
  const indexParaChave: Record<number, string> = {
    0: 'dom', 1: 'seg', 2: 'ter', 3: 'qua', 4: 'qui', 5: 'sex', 6: 'sab',
  };
  const chaveHoje = indexParaChave[diaJsIndex];

  // Normalizar funcao de apoio
  const normalizar = (s: string) =>
    s.trim().toLowerCase()
      .replace(/[áàã]/g, 'a').replace(/[éè]/g, 'e')
      .replace(/[íì]/g, 'i').replace(/[óò]/g, 'o').replace(/[úù]/g, 'u');

  // Coletar TODOS os segmentos do dia atual na string.
  // O formato pode ter multiplos segmentos do mesmo dia separados por "|"
  // (ex: "Ter: 07:00 - 12:00 | Ter: 13:30 - 18:00") quando gerado por versoes
  // mais antigas do backend, ou um unico segmento com intervalos separados por
  // virgula (ex: "Ter: 07:00 - 12:00, 13:30 - 18:00"). Ambos os formatos sao
  // suportados agregando todos os segmentos do dia antes de verificar.
  const partes = raw.split('|').map(p => p.trim());
  const segmentosDoDia = partes.filter(parte => {
    const sep = parte.indexOf(':');
    if (sep === -1) return false;
    const diaNorm = normalizar(parte.substring(0, sep));
    return DIAS_ALIAS_MAP[diaNorm] === chaveHoje;
  });

  if (segmentosDoDia.length === 0) return false;

  // Agregar todos os intervalos de todos os segmentos do dia em uma lista unica
  const todosOsIntervalos: string[] = [];
  for (const segmento of segmentosDoDia) {
    const valorHorarios = segmento.substring(segmento.indexOf(':') + 1).trim();
    if (!valorHorarios) continue;
    if (valorHorarios.toLowerCase() === 'cerrado' || valorHorarios.toLowerCase() === 'fechado') return false;
    for (const intervaloRaw of valorHorarios.split(',')) {
      todosOsIntervalos.push(intervaloRaw.trim());
    }
  }

  if (todosOsIntervalos.length === 0) return false;

  // Hora atual em minutos desde meia-noite (fuso do estabelecimento)
  const minutosAgora = hora * 60 + minuto;

  // Verificar se hora atual esta dentro de algum intervalo
  return todosOsIntervalos.some(intervalo => {
    // Separar "HH:MM - HH:MM" usando regex que captura o hifen com espacos opcionais
    const partesSep = intervalo.split(/\s*-\s*/);
    if (partesSep.length !== 2) return false;
    try {
      const [hIni, mIni] = partesSep[0].trim().split(':').map(Number);
      const [hFim, mFim] = partesSep[1].trim().split(':').map(Number);
      const minutosInicio = hIni * 60 + mIni;
      const minutosFim = hFim * 60 + mFim;
      return minutosAgora >= minutosInicio && minutosAgora <= minutosFim;
    } catch {
      return false;
    }
  });
}

/**
 * Parsea a string horarioFuncionamento do backend em uma lista de HorarioDia.
 * Formato esperado: "Seg: 08:00 - 18:00 | Ter: 08:00 - 18:00 | ..."
 *
 * @param raw - String bruta de horarioFuncionamento do backend
 * @param translate - Instancia do TranslateService para traduzir nomes dos dias
 * @returns Lista de 7 HorarioDia ordenados de segunda a domingo
 */
export function parseHorariosFuncionamento(raw: string | null | undefined, translate: TranslateService): HorarioDia[] {
  // Usar dia da semana no fuso do estabelecimento para marcar o dia atual corretamente
  const hoje = obterAgoraEmAsuncion().diaIndex; // 0=domingo
  const fechadoLabel = translate.instant('cardapio_digital.sidebar.fechado_label');

  // Mapa de chave interna -> horario(s)
  const horarioMap: Record<string, string> = {};

  if (raw) {
    // Parsear string "Seg: 08:00 - 18:00 | Ter: 08:00 - 18:00 | ..."
    const partes = raw.split('|').map(p => p.trim());

    for (const parte of partes) {
      const separador = parte.indexOf(':');
      if (separador === -1) continue;
      const diaRaw = parte.substring(0, separador).trim().toLowerCase();
      const horario = parte.substring(separador + 1).trim();
      // Normalizar: remover acentos
      const diaNorm = diaRaw.replace(/[áàã]/g, 'a').replace(/[éè]/g, 'e').replace(/[íì]/g, 'i').replace(/[óò]/g, 'o').replace(/[úù]/g, 'u');
      // Mapear para chave interna (seg, ter, qua, ...)
      const chave = DIAS_ALIAS_MAP[diaNorm];
      if (chave) {
        // Se ja tem horario, concatenar (ex: dois turnos)
        horarioMap[chave] = horarioMap[chave] ? `${horarioMap[chave]}, ${horario}` : horario;
      }
    }
  }

  // Monta lista dos 7 dias na ordem correta
  return DIAS_ORDEM.map(diaKey => {
    const horarioStr = horarioMap[diaKey];
    const diaIndex = DIAS_INDEX_MAP[diaKey] ?? -1;
    const isAtual = diaIndex === hoje;
    const isFechado = !horarioStr || horarioStr.toLowerCase() === 'cerrado' || horarioStr.toLowerCase() === 'fechado';
    const diaTraducao = translate.instant(`cardapio_digital.sidebar.dias.${diaKey}`);

    return {
      dia: diaTraducao,
      horario: isFechado ? fechadoLabel : horarioStr,
      fechado: isFechado,
      atual: isAtual,
    };
  });
}
